/**
 * botAi — Pure domain decision logic for in-game bot actions.
 *
 * Behavior Tree logic (deterministic):
 * 1. Low HP (<20% always, or <35% vs nearby enemy champ) -> Retreat to fountain.
 * 2. Enemy in attack range -> Attack target.
 * 3. Visible enemy in vision radius -> Chase/move toward enemy.
 * 4. Allied minions nearby -> Follow minion wave.
 * 5. Otherwise -> Advance toward enemy base spawn.
 *
 * Pure JS — no Three.js, no DOM.
 *
 * Contrato público:
 *   updateBotAi(bot, allEntities, mapConfig, dt) → { action, targetEntity?, targetX?, targetZ? }
 */

import { ARAM_CONFIG } from '../map/aramRules.js';
import { defaultChampionVisionRadius } from '../vision.js';
import { alliedWaveFollowTarget } from './botWaveFollow.js';
import { botAdvanceTarget } from './botObjectiveBias.js';
import { pickInRangeAttackTarget } from './botTargetPriority.js';
import { shouldRefuseDive } from './botTurretDive.js';
import { shouldRetreatToFountain } from './botNoFeedRetreat.js';
import { alliedRelicSeekTarget } from './botRelicSeek.js';
import { botCombatDecision } from './botCast.js';
import { botIgniteDecision } from './botIgnite.js';
import { botFlashRetreatDecision } from './botFlashRetreat.js';
import { stickCombatTarget } from './botStickyTarget.js';

function getDistance2D(posA, posB) {
  if (!posA || !posB) return Infinity;
  const az = posA.z !== undefined ? posA.z : (posA.y !== undefined ? posA.y : 0);
  const bz = posB.z !== undefined ? posB.z : (posB.y !== undefined ? posB.y : 0);
  return Math.hypot(posB.x - posA.x, bz - az);
}

/**
 * Resolves spawn location for a given team from mapConfig.
 */
function getTeamSpawn(team, mapConfig = {}) {
  const chaos = team === 'red' || team === 'chaos';
  const fromMap = chaos ? mapConfig.redSpawn : mapConfig.blueSpawn;
  if (fromMap && Number.isFinite(Number(fromMap.x))) {
    return { x: fromMap.x, z: fromMap.z ?? fromMap.y ?? 0 };
  }
  const pad = chaos ? ARAM_CONFIG.spawnPlatforms.red : ARAM_CONFIG.spawnPlatforms.blue;
  return { x: pad.x, z: pad.z };
}

/** Combat uses attackRange/100 (see attackCycleStateMachine). */
export function sandboxAttackRange(bot) {
  const raw = Number(bot?.attackRange);
  if (!Number.isFinite(raw) || raw <= 0) return 1.25;
  return raw / 100;
}

function sandboxVisionRadius(bot) {
  const raw = Number(bot?.visionRadius);
  if (Number.isFinite(raw) && raw > 0) return raw;
  return defaultChampionVisionRadius();
}

/**
 * Resolves target base destination to attack for a given team.
 */
function getEnemySpawn(team, mapConfig = {}) {
  const chaos = team === 'red' || team === 'chaos';
  return getTeamSpawn(chaos ? 'blue' : 'red', mapConfig);
}

/**
 * Computes decision for a single bot entity.
 * @param {object} bot
 * @param {object[]} allEntities
 * @param {object} mapConfig
 * @param {number} _dt
 * @returns {{ action: 'attack'|'move'|'idle', targetEntity?: object, targetX?: number, targetZ?: number }}
 */
export function updateBotAi(bot, allEntities = [], mapConfig = {}, _dt = 0.05) {
  if (!bot || bot.isDead) {
    return { action: 'idle' };
  }

  // 1. Low HP: <20% always, or <35% with a nearby enemy champ (FID-BOT-16)
  if (shouldRetreatToFountain(bot, allEntities, sandboxVisionRadius(bot))) {
    const ownSpawn = getTeamSpawn(bot.team, mapConfig);
    const flash = botFlashRetreatDecision(
      bot,
      allEntities,
      ownSpawn,
      sandboxAttackRange(bot)
    );
    if (flash) return flash;
    const targetX = ownSpawn.x;
    const targetZ = ownSpawn.z !== undefined ? ownSpawn.z : ownSpawn.y;
    return { action: 'move', targetX, targetZ };
  }

  const enemies = allEntities.filter(
    (e) => e && !e.isDead && e.team !== bot.team
  );

  const ignite = botIgniteDecision(bot, enemies);
  if (ignite) return ignite;

  // Find closest enemy
  let closestEnemy = null;
  let closestDist = Infinity;

  for (const enemy of enemies) {
    const dist = getDistance2D(bot, enemy);
    if (dist < closestDist) {
      closestDist = dist;
      closestEnemy = enemy;
    }
  }

  // 2. Enemy in attack range -> Attack (champ > minion > structure, FID-BOT-13)
  const attackRange = sandboxAttackRange(bot);
  let attackTarget = pickInRangeAttackTarget(bot, enemies, attackRange);
  if (attackTarget && shouldRefuseDive(bot, attackTarget, allEntities)) {
    attackTarget = null;
  }
  attackTarget = stickCombatTarget(bot, attackTarget);
  if (attackTarget) {
    bot.currentTarget = attackTarget;
    return botCombatDecision(bot, attackTarget);
  }

  // 3. Enemy in vision radius -> Chase (skip turret dives without wave, FID-BOT-14)
  const visionRadius = sandboxVisionRadius(bot);
  if (closestEnemy && closestDist <= visionRadius && !shouldRefuseDive(bot, closestEnemy, allEntities)) {
    const enemyZ = closestEnemy.z !== undefined ? closestEnemy.z : closestEnemy.y;
    return { action: 'move', targetX: closestEnemy.x, targetZ: enemyZ };
  }

  // 4. Hurt: walk to allied relic pad (FID-BOT-18)
  const relic = alliedRelicSeekTarget(bot, mapConfig.relics || []);
  if (relic) {
    return { action: 'move', targetX: relic.x, targetZ: relic.z };
  }

  // 5. Allied minions nearby -> Follow minion wave (FID-BOT-10)
  const wave = alliedWaveFollowTarget(bot, allEntities);
  if (wave) {
    return { action: 'move', targetX: wave.x, targetZ: wave.z };
  }

  // 6. Otherwise -> Advance toward enemy base (objective-biased, FID-BOT-11)
  const enemySpawn = getEnemySpawn(bot.team, mapConfig);
  const dest = botAdvanceTarget(bot, enemySpawn);
  return { action: 'move', targetX: dest.x, targetZ: dest.z };
}
